import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid2";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Stack,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { selectUserToken } from "../../../../features/userSlice";
import { AdvisorMapsEndpointLogic } from "../../../../logic/DataCollection/Advisor/AdvisorMapsEndPointLogic";
import {
  ADVISOR_GAP_RULE_NOTE,
  ADVISOR_GAP_RULE_PLACEHOLDER,
  AdvisorGapRule,
  advisorCanonicalMetrics,
} from "../helpers/advisorStaticData";
import "../Advisor.css";

type AdvisorOption = { id: number; label: string };
type SortDirection = "asc" | "desc";

const tableHeaders = [
  { id: 0, KEY: "metric", label: "Metric", sortable: true },
  { id: 1, KEY: "gapRule", label: "Gap Rule", sortable: true },
  { id: 2, KEY: "derivations", label: "Derivations", sortable: true },
  { id: 3, KEY: "dependsOn", label: "Depends On", sortable: true },
];

const AdvisorCreateMap = () => {
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const interfaceData = useSelector(
    (state: any) => state.advisor?.interfaceData,
  );
  const destinationTablesData = useSelector(
    (state: any) => state.advisor?.destinationTablesData,
  );
  const gapRulesData: AdvisorGapRule[] = useSelector(
    (state: any) => state.advisor?.gapRulesData,
  );

  const [filtersLoading, setFiltersLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [mapName, setMapName] = React.useState("");
  const [selectedInterface, setSelectedInterface] =
    React.useState<AdvisorOption | null>(null);
  const [selectedDestinationTable, setSelectedDestinationTable] =
    React.useState<AdvisorOption | null>(null);
  /** a new map starts with every metric coming straight from the source */
  const [selectedGapRules, setSelectedGapRules] = React.useState<
    Record<string, AdvisorGapRule | null>
  >({});
  const [sort, setSort] = React.useState<{
    keytoSort: string;
    direction: SortDirection;
  }>({ keytoSort: "", direction: "asc" });

  React.useEffect(() => {
    const fetchFilters = async () => {
      setFiltersLoading(true);
      try {
        await Promise.all([
          AdvisorMapsEndpointLogic.getInterface(oauthTokenHeader),
          AdvisorMapsEndpointLogic.getDestinationTables(oauthTokenHeader),
          AdvisorMapsEndpointLogic.getGapRules(oauthTokenHeader),
        ]);
      } finally {
        setFiltersLoading(false);
      }
    };
    fetchFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const interfaceOptions: AdvisorOption[] = React.useMemo(
    () =>
      interfaceData?.map((item: any) => ({
        label: item?.affiliate_system_interface_name,
        id: item?.id,
      })) || [],
    [interfaceData],
  );

  const destinationTableOptions: AdvisorOption[] = React.useMemo(
    () =>
      destinationTablesData?.map((item: any) => ({
        label: item?.destination_table,
        id: item?.id,
      })) || [],
    [destinationTablesData],
  );

  const isValidPayload = Boolean(
    mapName.trim() && selectedInterface && selectedDestinationTable,
  );

  const handleCreateMap = async () => {
    if (!isValidPayload || !selectedInterface || !selectedDestinationTable) {
      return;
    }
    const assignments = advisorCanonicalMetrics.reduce<Record<string, number>>(
      (accumulator, metric) => {
        accumulator[metric] = selectedGapRules[metric]?.id ?? 0;
        return accumulator;
      },
      {},
    );
    setSubmitting(true);
    try {
      const isSuccess = await AdvisorMapsEndpointLogic.createAdvisorMap(
        oauthTokenHeader,
        {
          name: mapName.trim(),
          backend_interface_id: selectedInterface.id,
          destination_table_id: selectedDestinationTable.id,
          assignments,
        },
      );
      if (isSuccess) {
        const currentParams = searchParams.toString();
        navigate(
          `/data_collection/advisor${currentParams ? `?${currentParams}` : ""}`,
          { replace: true },
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Rules are narrowed to this destination table and to the metric they target,
   * per the note above the table - so nothing shows until a table is chosen.
   */
  const getGapRuleOptions = React.useCallback(
    (metric: string) =>
      selectedDestinationTable
        ? (gapRulesData ?? []).filter(
            (rule) =>
              rule.destination_table_id === selectedDestinationTable.id &&
              rule.target_field === metric,
          )
        : [],
    [gapRulesData, selectedDestinationTable],
  );

  const handleSelectGapRule = (
    metric: string,
    gapRule: AdvisorGapRule | null,
  ) => {
    setSelectedGapRules((prev) => ({ ...prev, [metric]: gapRule }));
  };

  const handleSelectDestinationTable = (newValue: AdvisorOption | null) => {
    setSelectedDestinationTable(newValue);
    // rules belong to a destination table, so previous picks no longer apply
    setSelectedGapRules({});
  };

  const handleHeaderClick = (header: (typeof tableHeaders)[number]) => {
    if (!header.sortable) return;
    setSort((prev) => ({
      keytoSort: header.KEY,
      direction:
        prev.keytoSort === header.KEY && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const sortedMetrics = React.useMemo(() => {
    if (!sort.keytoSort) return advisorCanonicalMetrics;

    const getSortValue = (metric: string) => {
      const rule = selectedGapRules[metric];
      if (sort.keytoSort === "gapRule") return rule ? String(rule.id) : "";
      if (sort.keytoSort === "derivations") return rule?.rule_title ?? "";
      if (sort.keytoSort === "dependsOn") return rule?.dependency_field ?? "";
      return metric;
    };

    return advisorCanonicalMetrics.slice().sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);

      // metrics without a gap rule always sink to the bottom
      if (!aValue) return 1;
      if (!bValue) return -1;

      return sort.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  }, [sort, selectedGapRules]);

  /** how many metrics this destination can derive with a rule instead of the source */
  const derivableCount = advisorCanonicalMetrics.filter(
    (metric) => getGapRuleOptions(metric).length > 0,
  ).length;

  return (
    <Paper sx={{ mt: 2 }}>
      <Box sx={{ padding: "8px 16px" }}>
        <Typography variant="h3" sx={{ fontSize: "24px", mb: 2 }}>
          Create New Map
        </Typography>

        <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, md: 9 }}>
            <Stack direction="row" alignItems="flex-start" gap={2}>
              <TextField
                sx={{ width: "100%" }}
                label="Map Name"
                placeholder="Please enter map name"
                helperText="Mandatory Field"
                required
                color="secondary"
                size="small"
                value={mapName}
                onChange={(event) => setMapName(event.target.value)}
              />
              <Autocomplete
                sx={{ width: "100%" }}
                options={interfaceOptions}
                loading={filtersLoading}
                value={selectedInterface}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, newValue) => setSelectedInterface(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Interface"
                    placeholder={
                      filtersLoading
                        ? "Loading..."
                        : "Choose interface from the list."
                    }
                    helperText="Mandatory Field"
                    required
                    color="secondary"
                  />
                )}
                size="small"
              />
              <Autocomplete
                sx={{ width: "100%" }}
                options={destinationTableOptions}
                loading={filtersLoading}
                value={selectedDestinationTable}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, newValue) =>
                  handleSelectDestinationTable(newValue)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Destination Table"
                    placeholder={
                      filtersLoading
                        ? "Loading..."
                        : "Choose destination table from the list."
                    }
                    helperText="Mandatory Field"
                    required
                    color="secondary"
                  />
                )}
                size="small"
              />
            </Stack>
          </Grid>
          <Grid
            size={{ xs: 12, md: 3 }}
            container
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              color="secondary"
              disabled={!isValidPayload || submitting}
              onClick={handleCreateMap}
              data-tracker="953"
              sx={{ whiteSpace: "nowrap" }}
            >
              Create MAP
              {submitting ? (
                <CircularProgress
                  size={16}
                  color="inherit"
                  sx={{ ml: "8px" }}
                />
              ) : (
                <AddIcon sx={{ width: "20px", ml: "5px" }} />
              )}
            </Button>
          </Grid>
        </Grid>

        <Alert
          severity="warning"
          icon={<ErrorOutlineIcon fontSize="small" />}
          sx={{
            mb: 1,
            color: "warning.main",
            "& .MuiAlert-icon": { color: "warning.main", alignItems: "center" },
            "& .MuiAlert-message": { fontWeight: 500, fontSize: "14px" },
          }}
        >
          {ADVISOR_GAP_RULE_NOTE}
        </Alert>
      </Box>

      <TableContainer className="advisorCreateMapTable">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedMetrics.map((metric) => {
              const selectedRule = selectedGapRules[metric] || null;

              return (
                <TableRow
                  key={metric}
                  sx={{
                    "& > *": {
                      borderBottom:
                        "1px solid rgba(224, 224, 224, 1) !important",
                      padding: "10px 16px",
                    },
                  }}
                >
                  <TableCell align="left">{metric}</TableCell>
                  <TableCell align="left">
                    <Autocomplete
                      sx={{ width: "210px" }}
                      options={getGapRuleOptions(metric)}
                      getOptionLabel={(option) => option.id.toString()}
                      value={selectedRule}
                      loading={filtersLoading}
                      disabled={!selectedDestinationTable}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      onChange={(_, newValue) =>
                        handleSelectGapRule(metric, newValue)
                      }
                      noOptionsText="No gap rule for this metric"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            selectedDestinationTable
                              ? ADVISOR_GAP_RULE_PLACEHOLDER
                              : "Select a destination table"
                          }
                          color="secondary"
                        />
                      )}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="left">
                    {selectedRule ? (
                      <Typography component="span" sx={{ fontSize: "14px" }}>
                        {selectedRule.rule_title}
                      </Typography>
                    ) : (
                      <Typography
                        component="span"
                        sx={{ fontSize: "14px", color: "text.secondary" }}
                      >
                        from source
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {/* dependency_field arrives comma separated, no spaces */}
                    {selectedRule?.dependency_field
                      ? selectedRule.dependency_field.split(",").join(", ")
                      : "-"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ padding: "8px 16px" }}>
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          {`${derivableCount} of ${advisorCanonicalMetrics.length} metrics derived by a rule`}
        </Typography>
      </Box>
    </Paper>
  );
};

export default AdvisorCreateMap;
